/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../gql/graphql';

import type { TaxProfileFieldFragmentFragment } from './fragment.generated';
import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
/**
 * A personal or tax document accepted in one country's context: the
 * prefix is that country, so `PE_PASSPORT` is a passport presented to a
 * Peruvian bank or SUNAT. Behavior per type lives in its handler
 * (`backend.resources.identifiers`).
 */
export type IdentifierTypeEnum =
  | 'AR_CUIT'
  | 'AR_DNI'
  | 'CL_RUT'
  | 'CO_CC'
  | 'CO_FOREIGNER_ID'
  | 'CO_NIT'
  | 'MX_RFC'
  | 'PE_DNI'
  | 'PE_FOREIGNER_CARD'
  | 'PE_PASSPORT'
  | 'PE_RUC';

/** An enumeration. */
export type PersonKindEnum =
  | 'COMPANY'
  | 'INDIVIDUAL';

/**
 * Where a tax profile form field is stored: each value is the
 * `TaxProfile` attribute it fills, so completeness can read it back.
 */
export type TaxProfileFieldKeyEnum =
  | 'ADDRESS'
  | 'ADDRESS_LINE'
  | 'BUSINESS_ACTIVITY'
  | 'EMAIL'
  | 'IS_LARGE_TAXPAYER'
  | 'IS_MUNICIPAL_WITHHOLDING_AGENT'
  | 'LEGAL_NAME'
  | 'TAX_ID'
  | 'TAX_REGIME';

/**
 * How a tax profile form field is rendered and validated. The apps have
 * one component per kind and never branch on the country.
 */
export type TaxProfileFieldKindEnum =
  | 'ADDRESS'
  | 'BOOLEAN'
  | 'EMAIL'
  | 'IDENTIFIER'
  | 'REGIME'
  | 'TEXT';

/** An enumeration. */
export type TaxProfileOwnerKindEnum =
  | 'BRAND'
  | 'CREATOR'
  | 'WORKSPACE';

/**
 * The owner's fiscal status: how a creator gets paid, or what document a
 * payer (brand/workspace) receives. Members name the person kind, not the
 * document -- the document (boleta/factura/recibo) is derived per-country
 * copy on `TaxRegimeOption`, not part of the code.
 */
export type TaxRegimeEnum =
  | 'AR_MONOTRIBUTO'
  | 'AR_RESPONSABLE_INSCRIPTO'
  | 'CL_COMPANY'
  | 'CL_NATURAL_PERSON'
  | 'CO_COMPANY'
  | 'CO_NATURAL_PERSON'
  | 'CO_NON_INVOICING'
  | 'CO_ORDINARY'
  | 'CO_SIMPLE'
  | 'PE_COMPANY'
  | 'PE_NATURAL_PERSON';

/** An enumeration. */
export type WalletWithdrawalInvoiceTypeEnum =
  | 'HONORARIOS_RECEIPT'
  | 'INVOICE';

export type TaxProfileRequirementsQueryVariables = Exact<{
  countryCode: string;
  ownerKind?: Types.TaxProfileOwnerKindEnum | null | undefined;
}>;


export type TaxProfileRequirementsQuery = { fiscal: { __typename: 'FiscalQuery', taxProfileRequirements: { __typename: 'FiscalRequirementsType', countryCode: string, isSupported: boolean, unsupportedMessage: string, identifierLabel: string, fields: Array<(
        { __typename: 'TaxProfileFieldType' }
        & TaxProfileFieldFragmentFragment
      )>, regimes: Array<{ __typename: 'TaxRegimeOptionType', code: Types.TaxRegimeEnum, label: string, description: string, payerLabel: string, payerDescription: string, personKind: Types.PersonKindEnum, treatment: Types.WalletWithdrawalInvoiceTypeEnum, withholds: boolean, highlights: Array<string>, acknowledgmentLabel: string, consequence: string, fields: Array<(
          { __typename: 'TaxProfileFieldType' }
          & TaxProfileFieldFragmentFragment
        )> }> } } };

export type TaxIdRegistryMatchQueryVariables = Exact<{
  taxId: string;
}>;


export type TaxIdRegistryMatchQuery = { fiscal: { __typename: 'FiscalQuery', taxIdRegistryMatch: { __typename: 'TaxIdRegistryMatchType', legalName: string, businessActivity: string } | null } };

export type IdentifierTypesQueryVariables = Exact<{
  countryCode: string;
}>;


export type IdentifierTypesQuery = { fiscal: { __typename: 'FiscalQuery', identifierTypes: Array<{ __typename: 'IdentifierTypeOptionType', type: Types.IdentifierTypeEnum, label: string, example: string, maxLength: number, numeric: boolean }> } };


export const TaxProfileRequirementsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"taxProfileRequirements"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"countryCode"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"ownerKind"}},"type":{"kind":"NamedType","name":{"kind":"Name","value":"TaxProfileOwnerKindEnum"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"fiscal"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"taxProfileRequirements"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"countryCode"},"value":{"kind":"Variable","name":{"kind":"Name","value":"countryCode"}}},{"kind":"Argument","name":{"kind":"Name","value":"ownerKind"},"value":{"kind":"Variable","name":{"kind":"Name","value":"ownerKind"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"countryCode"}},{"kind":"Field","name":{"kind":"Name","value":"isSupported"}},{"kind":"Field","name":{"kind":"Name","value":"unsupportedMessage"}},{"kind":"Field","name":{"kind":"Name","value":"identifierLabel"}},{"kind":"Field","name":{"kind":"Name","value":"fields"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"FragmentSpread","name":{"kind":"Name","value":"TaxProfileFieldFragment"}}]}},{"kind":"Field","name":{"kind":"Name","value":"regimes"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"code"}},{"kind":"Field","name":{"kind":"Name","value":"label"}},{"kind":"Field","name":{"kind":"Name","value":"description"}},{"kind":"Field","name":{"kind":"Name","value":"payerLabel"}},{"kind":"Field","name":{"kind":"Name","value":"payerDescription"}},{"kind":"Field","name":{"kind":"Name","value":"personKind"}},{"kind":"Field","name":{"kind":"Name","value":"treatment"}},{"kind":"Field","name":{"kind":"Name","value":"withholds"}},{"kind":"Field","name":{"kind":"Name","value":"highlights"}},{"kind":"Field","name":{"kind":"Name","value":"acknowledgmentLabel"}},{"kind":"Field","name":{"kind":"Name","value":"consequence"}},{"kind":"Field","name":{"kind":"Name","value":"fields"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"FragmentSpread","name":{"kind":"Name","value":"TaxProfileFieldFragment"}}]}}]}}]}}]}}]}},{"kind":"FragmentDefinition","name":{"kind":"Name","value":"TaxProfileFieldFragment"},"typeCondition":{"kind":"NamedType","name":{"kind":"Name","value":"TaxProfileFieldType"}},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"key"}},{"kind":"Field","name":{"kind":"Name","value":"kind"}},{"kind":"Field","name":{"kind":"Name","value":"label"}},{"kind":"Field","name":{"kind":"Name","value":"placeholder"}},{"kind":"Field","name":{"kind":"Name","value":"helpText"}},{"kind":"Field","name":{"kind":"Name","value":"required"}},{"kind":"Field","name":{"kind":"Name","value":"identifierTypes"}},{"kind":"Field","name":{"kind":"Name","value":"registryLookup"}}]}}]} as unknown as DocumentNode<TaxProfileRequirementsQuery, TaxProfileRequirementsQueryVariables>;
export const TaxIdRegistryMatchDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"taxIdRegistryMatch"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"taxId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"fiscal"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"taxIdRegistryMatch"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"taxId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"taxId"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"legalName"}},{"kind":"Field","name":{"kind":"Name","value":"businessActivity"}}]}}]}}]}}]} as unknown as DocumentNode<TaxIdRegistryMatchQuery, TaxIdRegistryMatchQueryVariables>;
export const IdentifierTypesDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"identifierTypes"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"countryCode"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"fiscal"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"identifierTypes"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"countryCode"},"value":{"kind":"Variable","name":{"kind":"Name","value":"countryCode"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"type"}},{"kind":"Field","name":{"kind":"Name","value":"label"}},{"kind":"Field","name":{"kind":"Name","value":"example"}},{"kind":"Field","name":{"kind":"Name","value":"maxLength"}},{"kind":"Field","name":{"kind":"Name","value":"numeric"}}]}}]}}]}}]} as unknown as DocumentNode<IdentifierTypesQuery, IdentifierTypesQueryVariables>;